import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import EquipmentIcon from './EquipmentIcon';

interface Material {
  id: number;
  materialId: number;
  materialName: string;
  materialType: string;
  materialRarity: string;
  description: string;
  baseValue: number;
  iconName: string;
  quantity: number;
  acquiredAt: string;
}

interface Recipe {
  id: number;
  name: string;
  description: string;
  resultItemType: string | number;
  resultEquipmentSlot?: number | null;
  resultItemName: string;
  requiredLevel: number;
  requiredClass: string | null;
  craftingTime: number;
  experienceReward: number;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  mpBonus: number;
  speedBonus: number;
  // Weapon properties
  minDamage?: number;
  maxDamage?: number;
  minRange?: number;
  maxRange?: number;
  damageType?: string;
  aoeShape?: string;
  requiresLoS?: boolean;
  weaponAPCost?: number;
  accuracyModifier?: number;
  usesAmmo?: boolean;
  maxAmmo?: number;
  imageUrl?: string;
  iconName?: string;
  discoveredAt: string;
  ingredients: Array<{
    materialId: number;
    materialName: string;
    materialType: string;
    materialRarity: string;
    quantity: number;
  }>;
}

interface CraftedItem {
  id: number;
  itemName: string;
  itemType: string;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  mpBonus: number;
  speedBonus: number;
  specialEffects: string;
  durability: number;
  maxDurability: number;
  isEquipped: boolean;
  equippedToCharacterName: string | null;
  recipeName: string;
  craftedAt: string;
  imageUrl?: string;
  iconName?: string;
}

interface MaterialSource {
  monsterId: number;
  monsterName: string;
  monsterLevel: number;
  monsterDifficulty: string;
  dropChance: number;
  minQuantity: number;
  maxQuantity: number;
  minPlayerLevel: number;
  maxPlayerLevel: number;
}

type ActiveTab = 'inventory' | 'recipes' | 'crafted-items';

const Crafting: React.FC = () => {
  const { handleAuthError } = useAuth();
  const { addNotification } = useNotifications();
  const { checkAndStartTutorial } = useTutorial();
  const [activeTab, setActiveTab] = useState<ActiveTab>('inventory');
  const [inventory, setInventory] = useState<Material[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [craftedItems, setCraftedItems] = useState<CraftedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [crafting, setCrafting] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSlot, setFilterSlot] = useState<string>('all');
  const [filterClass, setFilterClass] = useState<string>('all');
  const [showOnlyCraftable, setShowOnlyCraftable] = useState<boolean>(false);
  const [recipeStats, setRecipeStats] = useState<{totalRecipes: number, discoveredRecipes: number, discoveryPercentage: number} | null>(null);
  const [materialSources, setMaterialSources] = useState<Record<number, MaterialSource[]>>({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading crafting data...');
      await Promise.all([
        loadInventory(),
        loadRecipes(),
        loadCraftedItems(),
        loadRecipeStats(),
        loadMaterialSources()
      ]);
      console.log('Crafting data loaded successfully');
    } catch (err) {
      console.error('Error loading crafting data:', err);
      setError('Failed to load crafting data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-trigger crafting tutorial for first-time players
  useEffect(() => {
    if (recipes.length > 0 && !loading) {
      checkAndStartTutorial('crafting-basics');
    }
  }, [recipes, loading, checkAndStartTutorial]);

  const loadInventory = useCallback(async () => {
    try {
      console.log('Loading inventory...');
      const response = await makeAuthenticatedRequest('/crafting/inventory');
      console.log('Inventory response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Inventory data:', data);
        setInventory(data);
      } else {
        const errorData = await response.json();
        console.error('Inventory error:', errorData);
        throw new Error(errorData.error || 'Failed to load inventory');
      }
    } catch (err) {
      console.error('Inventory catch error:', err);
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(`Inventory Error: ${apiError.message}`);
    }
  }, [handleAuthError]);

  const loadRecipes = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/recipes');
      if (response.ok) {
        const data = await response.json();
        setRecipes(data);
      } else {
        throw new Error('Failed to load recipes');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    }
  }, [handleAuthError]);

  const loadCraftedItems = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/crafted-items');
      if (response.ok) {
        const data = await response.json();
        setCraftedItems(data);
      } else {
        throw new Error('Failed to load crafted items');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    }
  }, [handleAuthError]);

  const loadRecipeStats = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/recipe-stats');
      if (response.ok) {
        const data = await response.json();
        setRecipeStats(data);
      } else {
        throw new Error('Failed to load recipe stats');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      console.error('Failed to load recipe stats:', err);
    }
  }, [handleAuthError]);

  const loadMaterialSources = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/material-sources');
      if (response.ok) {
        const data = await response.json();
        setMaterialSources(data);
      } else {
        throw new Error('Failed to load material sources');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      console.error('Failed to load material sources:', err);
    }
  }, [handleAuthError]);

  const craftItem = async (recipeId: number) => {
    if (crafting) return;
    
    setCrafting(true);
    setError(null);
    
    try {
      const response = await makeAuthenticatedRequest('/crafting/craft', {
        method: 'POST',
        body: JSON.stringify({ recipeId })
      });

      if (response.ok) {
        const result = await response.json();

        // Refresh data
        await loadData();

        // Show success message
        setError(null);

        // Show toast notification for crafting success
        const quality = result.craftedItem.qualityPercent || 100;
        let notificationType: 'info' | 'levelup' | 'achievement' = 'info';
        let title = 'Item Crafted!';

        if (quality >= 95) {
          notificationType = 'achievement';
          title = 'Perfect Craft!';
        } else if (quality >= 85) {
          notificationType = 'levelup';
          title = 'Excellent Craft!';
        }

        // Build bonus message if bonuses are active
        let bonusMessage = `You crafted ${result.craftedItem.itemName}! Quality: ${quality}%`;
        if (result.bonuses?.hasBonuses) {
          const bonusParts = [];
          if (result.bonuses.qualityBonus) {
            bonusParts.push(`Quality Bonus: ${result.bonuses.qualityBonus}`);
          }
          if (result.bonuses.materialsSaved > 0) {
            bonusParts.push(`Saved ${result.bonuses.materialsSaved} materials!`);
          }
          if (bonusParts.length > 0) {
            bonusMessage += ` 🏰 Town Bonuses: ${bonusParts.join(', ')}`;
          }
        }

        addNotification({
          type: notificationType,
          title: title,
          message: bonusMessage
        });

        // Show achievement notifications from backend
        if (result.achievementsUnlocked && result.achievementsUnlocked.length > 0) {
          result.achievementsUnlocked.forEach((achievement: any) => {
            addNotification({
              type: 'achievement',
              title: `${achievement.icon} Achievement Unlocked!`,
              message: `${achievement.name} - ${achievement.description} (${achievement.currentProgress}/${achievement.requiredValue})`
            });
          });
        }

      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to craft item');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setCrafting(false);
    }
  };

  const getRarityColor = (rarity: string): string => {
    switch (rarity.toLowerCase()) {
      case 'common': return '#9e9e9e';
      case 'uncommon': return '#4caf50';
      case 'rare': return '#2196f3';
      case 'epic': return '#9c27b0';
      case 'legendary': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getRarityIcon = (rarity: string): string => {
    switch (rarity.toLowerCase()) {
      case 'common': return '⚪';
      case 'uncommon': return '🟢';
      case 'rare': return '🔵';
      case 'epic': return '🟣';
      case 'legendary': return '🟠';
      default: return '⚪';
    }
  };

  const getRarityBorderColor = (rarity: string): string => {
    switch (rarity.toLowerCase()) {
      case 'common': return '#9e9e9e';
      case 'uncommon': return '#4caf50';
      case 'rare': return '#2196f3';
      case 'epic': return '#9c27b0';
      case 'legendary': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getTypeIcon = (type: string | number): string => {
    const typeString = getItemTypeString(type);
    switch (typeString.toLowerCase()) {
      case 'weapon': return '⚔️';
      case 'armor': return '🛡️';
      case 'accessory': return '💍';
      case 'consumable': return '🧪';
      case 'material': return '🔧';
      default: return '📦';
    }
  };

  const getItemTypeFromResult = (type: string | number): 'Weapon' | 'Armor' | 'Accessory' => {
    const typeString = getItemTypeString(type);
    switch (typeString.toLowerCase()) {
      case 'weapon': return 'Weapon';
      case 'armor': return 'Armor';
      case 'accessory': return 'Accessory';
      default: return 'Weapon'; // Default fallback
    }
  };

  const canCraftRecipe = (recipe: Recipe): boolean => {
    const inventoryMap = new Map(inventory.map(item => [item.materialId, item.quantity]));
    
    return recipe.ingredients.every(ingredient => {
      const available = inventoryMap.get(ingredient.materialId) || 0;
      return available >= ingredient.quantity;
    });
  };

  const getFilteredRecipes = (): Recipe[] => {
    return recipes.filter(recipe => {
      if (filterType !== 'all') {
        // Convert numeric enum to string for comparison
        const itemTypeString = getItemTypeString(recipe.resultItemType);
        if (itemTypeString.toLowerCase() !== filterType) {
          return false;
        }
      }
      if (filterSlot !== 'all') {
        const slotNumber = parseInt(filterSlot);

        // Debug logging
        console.log(`Filtering by slot ${slotNumber}, recipe ${recipe.name} has slot: ${recipe.resultEquipmentSlot}`);

        // Only filter if recipe has a slot defined and it doesn't match
        // This allows recipes without slots to pass through when "All Slots" is selected
        if (recipe.resultEquipmentSlot != null && recipe.resultEquipmentSlot !== slotNumber) {
          console.log(`  -> Filtered out: slot mismatch`);
          return false;
        }
        // If recipe has no slot but we're filtering by slot, hide it
        if (recipe.resultEquipmentSlot == null) {
          console.log(`  -> Filtered out: no slot defined`);
          return false;
        }
      }
      if (filterClass !== 'all' && recipe.requiredClass !== filterClass) {
        return false;
      }
      if (showOnlyCraftable && !canCraftRecipe(recipe)) {
        return false;
      }
      return true;
    });
  };

  const getItemTypeString = (itemType: string | number): string => {
    if (typeof itemType === 'string') {
      return itemType;
    }
    // Convert numeric enum to string
    const itemTypeMap: { [key: number]: string } = {
      0: 'weapon',
      1: 'armor',
      2: 'accessory',
      3: 'consumable',
      4: 'material',
      5: 'tool'
    };
    return itemTypeMap[itemType] || 'unknown';
  };

  const renderInventoryTab = () => (
    <div className="crafting-tab-content">
      <div className="inventory-header">
        <h3>Material Inventory</h3>
        <p>Materials collected from battles and exploration</p>
      </div>
      
      {inventory.length === 0 ? (
        <div className="empty-state">
          <p>No materials in inventory</p>
          <small>Defeat enemies in battle to collect crafting materials</small>
        </div>
      ) : (
        <div className="inventory-grid">
          {inventory.map(item => (
            <div 
              key={item.id} 
              className="inventory-item"
              style={{ 
                borderColor: getRarityBorderColor(item.materialRarity),
                boxShadow: `0 0 10px ${getRarityBorderColor(item.materialRarity)}40`
              }}
            >
              <div className="item-header">
                <div className="item-icon">
                  <span className="rarity-icon">{getRarityIcon(item.materialRarity)}</span>
                  📦
                </div>
                <div className="item-quantity">{item.quantity}</div>
              </div>
              <div className="item-details">
                <h4 style={{ color: getRarityColor(item.materialRarity) }}>
                  {item.materialName}
                </h4>
                <div className="item-type">{item.materialType}</div>
                <p className="item-description">{item.description}</p>
                <div className="item-stats">
                  <span className="item-value">Value: {item.baseValue}g</span>
                  <span 
                    className="item-rarity"
                    style={{ 
                      backgroundColor: getRarityColor(item.materialRarity),
                      color: '#ffffff'
                    }}
                  >
                    {getRarityIcon(item.materialRarity)} {item.materialRarity}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderRecipesTab = () => (
    <div className="crafting-tab-content">
      <div className="recipes-header">
        <h3>Known Recipes</h3>
        <div className="recipe-filters">
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setFilterSlot('all'); // Reset slot filter when type changes
            }}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="weapon">Weapons</option>
            <option value="armor">Armor</option>
            <option value="accessory">Accessories</option>
            <option value="consumable">Consumables</option>
          </select>

          <select
            value={filterSlot}
            onChange={(e) => setFilterSlot(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Slots</option>
            <optgroup label="Weapons">
              <option value="0">Main Hand</option>
              <option value="1">Off Hand</option>
            </optgroup>
            <optgroup label="Armor">
              <option value="2">Head</option>
              <option value="3">Chest</option>
              <option value="4">Legs</option>
              <option value="5">Feet</option>
              <option value="6">Hands</option>
            </optgroup>
            <optgroup label="Accessories">
              <option value="7">Necklace</option>
              <option value="8">Ring 1</option>
              <option value="9">Ring 2</option>
              <option value="10">Belt</option>
              <option value="11">Back</option>
            </optgroup>
          </select>

          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Classes</option>
            <option value="Warrior">Warrior</option>
            <option value="Mage">Mage</option>
            <option value="Ranger">Ranger</option>
            <option value="Cleric">Cleric</option>
            <option value="Rogue">Rogue</option>
          </select>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', marginLeft: '1rem' }}>
            <input
              type="checkbox"
              checked={showOnlyCraftable}
              onChange={(e) => setShowOnlyCraftable(e.target.checked)}
            />
            Show only craftable
          </label>
        </div>
      </div>

      {getFilteredRecipes().length === 0 ? (
        <div className="empty-state">
          <p>No recipes discovered yet</p>
          <small>Discover recipes through exploration and experimentation</small>
        </div>
      ) : (
        <div className="recipes-grid">
          {getFilteredRecipes().map(recipe => {
            const canCraft = canCraftRecipe(recipe);
            const craftedCount = craftedItems.filter(item => item.recipeName === recipe.name).length;
            return (
              <div key={recipe.id} className={`recipe-card ${canCraft ? 'craftable' : 'not-craftable'}`}>
                <div className="recipe-header">
                  <EquipmentIcon
                    imageUrl={recipe.imageUrl}
                    iconName={recipe.iconName}
                    itemName={recipe.name}
                    itemType={getItemTypeFromResult(recipe.resultItemType)}
                    size="large"
                    showTooltip={true}
                    stats={{
                      attackBonus: recipe.attackBonus,
                      defenseBonus: recipe.defenseBonus,
                      hpBonus: recipe.hpBonus,
                      mpBonus: recipe.mpBonus,
                      speedBonus: recipe.speedBonus
                    }}
                  />
                  <div className="recipe-info">
                    <h4>{recipe.name}</h4>
                    <div className="recipe-result">{recipe.resultItemName}</div>
                    {recipe.requiredClass && (
                      <div className="recipe-class">Requires: {recipe.requiredClass} Lv.{recipe.requiredLevel}</div>
                    )}
                    {craftedCount > 0 && (
                      <div className="recipe-crafted-count" style={{color: '#4caf50', fontSize: '0.9em', marginTop: '0.25rem'}}>
                        📦 {craftedCount} in inventory
                      </div>
                    )}
                  </div>
                </div>

                <p className="recipe-description">{recipe.description}</p>

                <div className="recipe-stats">
                  {recipe.attackBonus > 0 && <span className="stat">⚔️ +{recipe.attackBonus}</span>}
                  {recipe.defenseBonus > 0 && <span className="stat">🛡️ +{recipe.defenseBonus}</span>}
                  {recipe.hpBonus > 0 && <span className="stat">❤️ +{recipe.hpBonus}</span>}
                  {recipe.mpBonus > 0 && <span className="stat">💙 +{recipe.mpBonus}</span>}
                  {recipe.speedBonus > 0 && <span className="stat">⚡ +{recipe.speedBonus}</span>}
                </div>

                {/* Weapon Properties */}
                {(recipe.minDamage || recipe.maxDamage) && (
                  <div className="recipe-weapon-stats" style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                    borderRadius: '4px',
                    fontSize: '0.9em'
                  }}>
                    <div style={{ fontWeight: 'bold', color: '#FF9800', marginBottom: '0.25rem' }}>⚔️ Weapon Stats:</div>
                    {(recipe.minDamage || recipe.maxDamage) && (
                      <div>Damage: {recipe.minDamage || 0}-{recipe.maxDamage || 0} {recipe.damageType ? `(${recipe.damageType})` : ''}</div>
                    )}
                    {(recipe.minRange || recipe.maxRange) && (
                      <div>Range: {recipe.minRange || 1}-{recipe.maxRange || 1}</div>
                    )}
                    {recipe.weaponAPCost && recipe.weaponAPCost > 1 && (
                      <div>AP Cost: {recipe.weaponAPCost}</div>
                    )}
                    {recipe.aoeShape && recipe.aoeShape !== 'Single' && (
                      <div style={{ color: '#FF5722' }}>AOE: {recipe.aoeShape}</div>
                    )}
                    {recipe.usesAmmo && (
                      <div style={{ color: '#FFC107' }}>Ammo: {recipe.maxAmmo || 0}</div>
                    )}
                    {recipe.accuracyModifier !== undefined && recipe.accuracyModifier !== 0 && (
                      <div>Accuracy: {recipe.accuracyModifier > 0 ? '+' : ''}{recipe.accuracyModifier}%</div>
                    )}
                  </div>
                )}

                <div className="recipe-ingredients">
                  <h5>Required Materials:</h5>
                  {recipe.ingredients.map(ingredient => {
                    const available = inventory.find(item => item.materialId === ingredient.materialId)?.quantity || 0;
                    const hasEnough = available >= ingredient.quantity;
                    const sources = materialSources[ingredient.materialId] || [];

                    return (
                      <div
                        key={ingredient.materialId}
                        className={`ingredient ${hasEnough ? 'sufficient' : 'insufficient'}`}
                        style={{
                          borderColor: getRarityBorderColor(ingredient.materialRarity),
                          borderWidth: '1px',
                          borderStyle: 'solid'
                        }}
                      >
                        <span className="ingredient-name">
                          <span className="ingredient-rarity-icon">
                            {getRarityIcon(ingredient.materialRarity)}
                          </span>
                          <span style={{ color: getRarityColor(ingredient.materialRarity) }}>
                            {ingredient.materialName}
                          </span>
                        </span>
                        <span className="ingredient-quantity">
                          {available}/{ingredient.quantity}
                        </span>
                        <div className="ingredient-tooltip">
                          {sources.length > 0 ? (
                            <>
                              <div className="tooltip-header">Dropped by:</div>
                              {sources.slice(0, 5).map((source, idx) => (
                                <div key={idx} className="tooltip-source">
                                  <div className="source-monster">
                                    {source.monsterName} (Lv.{source.monsterLevel})
                                  </div>
                                  <div className="source-details">
                                    <span className={`difficulty-badge ${source.monsterDifficulty.toLowerCase()}`}>
                                      {source.monsterDifficulty}
                                    </span>
                                    <span className="drop-chance">{source.dropChance}% drop</span>
                                    <span className="drop-qty">{source.minQuantity}-{source.maxQuantity}x</span>
                                  </div>
                                </div>
                              ))}
                              {sources.length > 5 && (
                                <div className="tooltip-more">+{sources.length - 5} more</div>
                              )}
                            </>
                          ) : (
                            <div className="tooltip-header">No drop sources found</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="recipe-actions">
                  <div className="recipe-meta">
                    <span>⏱️ {recipe.craftingTime}s</span>
                    <span>📈 {recipe.experienceReward} XP</span>
                  </div>
                  <button 
                    className="craft-btn"
                    onClick={() => craftItem(recipe.id)}
                    disabled={!canCraft || crafting}
                  >
                    {crafting ? 'Crafting...' : 'Craft'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderCraftedItemsTab = () => (
    <div className="crafting-tab-content">
      <div className="crafted-items-header">
        <h3>Crafted Items</h3>
        <p>Items you have successfully crafted</p>
      </div>

      {craftedItems.length === 0 ? (
        <div className="empty-state">
          <p>No items crafted yet</p>
          <small>Craft your first item using discovered recipes</small>
        </div>
      ) : (
        <div className="crafted-items-grid">
          {craftedItems.map(item => (
            <div key={item.id} className={`crafted-item ${item.isEquipped ? 'equipped' : ''}`}>
              <div className="item-header">
                <EquipmentIcon
                  imageUrl={item.imageUrl}
                  iconName={item.iconName}
                  itemName={item.itemName}
                  itemType={getItemTypeFromResult(item.itemType)}
                  size="large"
                  showTooltip={true}
                  stats={{
                    attackBonus: item.attackBonus,
                    defenseBonus: item.defenseBonus,
                    hpBonus: item.hpBonus,
                    mpBonus: item.mpBonus,
                    speedBonus: item.speedBonus
                  }}
                />
                <div className="item-info">
                  <h4>{item.itemName}</h4>
                  <div className="item-type">{item.itemType}</div>
                  {item.isEquipped && (
                    <div className="equipped-badge">
                      Equipped to {item.equippedToCharacterName}
                    </div>
                  )}
                </div>
              </div>

              <div className="item-stats">
                {item.attackBonus > 0 && <span className="stat">⚔️ +{item.attackBonus}</span>}
                {item.defenseBonus > 0 && <span className="stat">🛡️ +{item.defenseBonus}</span>}
                {item.hpBonus > 0 && <span className="stat">❤️ +{item.hpBonus}</span>}
                {item.mpBonus > 0 && <span className="stat">💙 +{item.mpBonus}</span>}
                {item.speedBonus > 0 && <span className="stat">⚡ +{item.speedBonus}</span>}
              </div>

              <div className="item-durability">
                <div className="durability-bar">
                  <div 
                    className="durability-fill"
                    style={{ width: `${(item.durability / item.maxDurability) * 100}%` }}
                  ></div>
                </div>
                <span className="durability-text">
                  {item.durability}/{item.maxDurability} Durability
                </span>
              </div>

              <div className="item-meta">
                <span>From: {item.recipeName}</span>
                <span>Crafted: {new Date(item.craftedAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div className="crafting-loading">Loading crafting system...</div>;
  }

  return (
    <div className="crafting-container">
      <div className="crafting-header">
        <div>
          <h2>Crafting Workshop</h2>
          <p>Transform raw materials into powerful equipment and useful items</p>
        </div>
        {recipeStats && (
          <div className="recipe-discovery-stats">
            <div className="discovery-label">Recipes Discovered</div>
            <div className="discovery-count">{recipeStats.discoveredRecipes} / {recipeStats.totalRecipes}</div>
            <div className="discovery-percentage">{recipeStats.discoveryPercentage}%</div>
          </div>
        )}
      </div>

      <div className="crafting-tabs">
        <button 
          className={`tab-button ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          📦 Inventory ({inventory.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'recipes' ? 'active' : ''}`}
          onClick={() => setActiveTab('recipes')}
        >
          📜 Recipes ({recipes.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'crafted-items' ? 'active' : ''}`}
          onClick={() => setActiveTab('crafted-items')}
        >
          ⚔️ Crafted Items ({craftedItems.length})
        </button>
      </div>

      {error && (
        <div className="crafting-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="crafting-content">
        {activeTab === 'inventory' && renderInventoryTab()}
        {activeTab === 'recipes' && renderRecipesTab()}
        {activeTab === 'crafted-items' && renderCraftedItemsTab()}
      </div>
    </div>
  );
};

export default Crafting;